import { flowResult, makeAutoObservable, runInAction } from "mobx";
import { Money } from "../../core";
import { localizePath } from "../../locale";
import { hasDeliveryInfo, } from "../../ordering";
import { GeolocationTracking } from "../../tracking";
import { StreetAddressFormController } from "./StreetAddressFormController";
/**
 * Drives the "delivery checker" widget on `/dostawa` (and the vesspa home
 * hero): the eater enters an address and learns which restaurant delivers
 * there, at what cost. Replaces the legacy PureScript
 * `Components.DeliveryForm` + `Site.Components.DeliveryAddress` widgets.
 */
export class DeliveryCheckerController {
    constructor(params) {
        this._contacts = [];
        this.streetAddress = params.streetAddress;
        this.deliveryAddressStatus = params.deliveryAddressStatus;
        this.siteRestaurantsService = params.siteRestaurantsService;
        this.localizationProvider = params.localizationProvider;
        this.streetAddressStorageManager = params.streetAddressStorageManager;
        this.streetAddressController = new StreetAddressFormController({
            streetAddress: this.streetAddress,
            streetAddressAvailabilityService: params.streetAddressAvailabilityService,
        });
        makeAutoObservable(this);
    }
    get state() {
        return this.deliveryAddressStatus.state;
    }
    get isChecking() {
        return this.state.type === "Loading";
    }
    get hasGeocodingError() {
        return (this.state.type === "AddressDoesNotExistOrInaccurate" ||
            this.state.type === "NetworkError");
    }
    /** The address is valid and geocodable, but no restaurant delivers there. */
    get noRestaurantDelivers() {
        return this.state.type === "RestaurantDoesntDeliverTo";
    }
    get deliveringRestaurants() {
        const state = this.state;
        if (state.type !== "ChooseRestaurant" &&
            state.type !== "RestaurantDelivers") {
            return [];
        }
        return state.restaurants
            .filter(hasDeliveryInfo)
            .map((r) => this.toDeliveringRestaurant(r));
    }
    get contacts() {
        return this._contacts;
    }
    async check() {
        this.streetAddressController.submit();
        this.streetAddressStorageManager.store(this.streetAddress.storageData);
        await flowResult(this.deliveryAddressStatus.validate(null, this.streetAddress));
        GeolocationTracking.findAddressEvent("DeliveryChecker", this.state, this.streetAddress);
    }
    async loadContacts() {
        const restaurants = await this.siteRestaurantsService.getSiteRestaurants();
        runInAction(() => {
            this._contacts = restaurants.map((r) => ({
                name: r.name,
                phones: r.contactPhoneNumber !== null
                    ? r.contactPhoneNumber.split(",").map((s) => s.trim())
                    : [],
            }));
        });
    }
    toDeliveringRestaurant(restaurant) {
        var _a;
        const info = restaurant.deliveryInfo;
        return {
            id: restaurant.id,
            name: restaurant.name,
            orderPath: localizePath(`/restauracja/${(_a = restaurant.slug) !== null && _a !== void 0 ? _a : ""}`),
            deliveryCost: this.localizationProvider.formatPrice(Money.fromBackend(info.deliveryCost)),
            minOrderValue: this.localizationProvider.formatPrice(Money.fromBackend(info.minOrderValue)),
            freeDeliveryThreshold: info.freeDeliveryThreshold !== null
                ? this.localizationProvider.formatPrice(Money.fromBackend(info.freeDeliveryThreshold))
                : null,
            paymentMethods: restaurant.paymentMethods,
        };
    }
}
